import { useCallback, useMemo, useRef } from 'react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import { isPublicPublishPath } from '~publish/helpers/publicPublishPaths'
import {
  trackPublicCta,
  trackPublicCtaViewed,
} from '~publish/helpers/publicTracking'

import { useCommonTrackingProperties } from './useCommonTrackingProperties'

export type CtaProperties = {
  product?: string
  clientName?: string
  upgradePathName?: string
  extraProperties?: Record<string, unknown>
}

type CtaReturnValue = {
  clicked: () => void
  viewed: () => void
}

export const useCta = (cta: CTA, options?: CtaProperties): CtaReturnValue => {
  const {
    product = Product.Publish,
    clientName = Client.PublishWeb,
    upgradePathName = null,
    extraProperties = {},
  } = options ?? {}

  const organizationId = useOrganizationId()
  const commonTrackingProperties = useCommonTrackingProperties()
  const hasFiredViewed = useRef(false)

  const clicked = useCallback((): void => {
    if (isPublicPublishPath(window.location.pathname)) {
      trackPublicCta(cta)
      return
    }
    if (!organizationId || !commonTrackingProperties) return
    BufferTracker.cTAClicked({
      clientName,
      product,
      cta,
      upgradePathName,
      ...commonTrackingProperties,
      ...extraProperties,
    })
  }, [
    organizationId,
    clientName,
    product,
    cta,
    upgradePathName,
    commonTrackingProperties,
    extraProperties,
  ])

  const viewed = useCallback((): void => {
    if (isPublicPublishPath(window.location.pathname)) {
      if (!hasFiredViewed.current && trackPublicCtaViewed(cta))
        hasFiredViewed.current = true
      return
    }
    if (!organizationId || !upgradePathName || !commonTrackingProperties) return
    if (hasFiredViewed.current) return
    BufferTracker.upgradePathViewed({
      clientName,
      product,
      cta,
      upgradePathName,
      ...commonTrackingProperties,
      ...extraProperties,
    })
    hasFiredViewed.current = true
  }, [
    organizationId,
    clientName,
    product,
    cta,
    upgradePathName,
    commonTrackingProperties,
    extraProperties,
  ])

  return useMemo(() => ({ clicked, viewed }), [clicked, viewed])
}
